import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  appendPayloadListItem,
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  normalizePayloadList,
  updatePayloadListItem,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { customerSegmentsTooltip } from "../../../constants/TooltipInformation";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const Segment2 = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [segmentCode, setSegmentCode] = useState<string | null>(null);
  const [segmentCodeError, setSegmentCodeError] = useState<boolean>(false);
  const [segmentName, setSegmentName] = useState<string | null>(null);
  const [segmentNameError, setSegmentNameError] = useState<boolean>(false);
  const [parentSegment, setParentSegment] = useState<string | null>(null);
  const [segmentList, setSegmentList] = useState<string[]>([]);
  const [routeLocalCalls, setRouteLocalCalls] = useState<any>(null);
  const [guestTypeCode, setGuestTypeCode] = useState<any>(null);
  const [guestTypeList, setGuestTypeList] = useState<string[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [segment2, setSegment2] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const segmentPayload = {
    code: segmentCode,
    name: segmentName,
    isRouteLocalCalls: routeLocalCalls ?? false,
    parentSegment1Code: parentSegment ?? 0,
    guestTypeCode: guestTypeCode ?? 0,
    isActive: active ?? true,
  };

  const addCustomerSegmentInformation = () => {
    if (validateInput()) {
      if (!segmentCode || segmentCode === null || segmentCode === "") {
        setSegmentCodeError(true);
      }
      if (!segmentName || segmentName === null || segmentName === "") {
        setSegmentNameError(true);
      } else if (
        checkDuplicate(segment2, DNDFieldsEnum.SEGMENT2CODE, segmentCode)
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setSegment2((prev: any) => appendPayloadListItem(prev, segmentPayload));
        setLastUpdatedRecord(segmentPayload);
        editSegment2(null);
        setEditClicked(false);
      }
    }
  };

  const editSegment2 = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setSegmentCode(list?.code);
    setSegmentName(list?.name);
    setRouteLocalCalls(list?.isRouteLocalCalls);
    setParentSegment(list?.parentSegment1Code);
    setGuestTypeCode(list?.guestTypeCode);
    setActive(list ? list.isActive : true);
  };

  const updateSegment2 = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        (normalizePayloadList(segment2) ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.SEGMENT2CODE,
        segmentCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setSegment2((prev: any) =>
      updatePayloadListItem(prev, selectedId, segmentPayload),
    );
    editSegment2(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.Segment2} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteCustomerSegmentInformation = (id: number) => {
    setSegment2(deleteItem(segment2, id));
  };

  const validateInput = () => {
    setSegmentCodeError(false);
    setSegmentNameError(false);
    let isValid = true;
    if (
      segmentCode &&
      !validateString(
        segmentCode,
        validationLength.customerSegment2,
        validationType.CODE,
      )
    ) {
      setSegmentCodeError(true);
      isValid = false;
    }
    if (
      segmentName &&
      !validateString(
        segmentName,
        validationLength.customerDescription,
        validationType.OTHER,
      )
    ) {
      setSegmentNameError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(segment2);
    editClicked &&
      setSelectedId(checkData(segment2, selectedId) ? selectedId : null);
  }, [segment2]);

  useEffect(() => {
    validateInput();
  }, [segmentCode, segmentName]);

  useEffect(() => {
    if (fieldValues) {
      setSegment2(normalizePayloadList(fieldValues[RoomPropertyEnum.Segment2]));
      setGuestTypeList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.guestType],
          DNDFieldsEnum.GUESTTYPECODE,
          DNDFieldsEnum.GUESTTYPENAME,
        ),
      );
      setSegmentList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.Segment1],
          DNDFieldsEnum.SEGMENT1CODE,
          DNDFieldsEnum.SEGMENT1NAME,
        ),
      );
    }
  }, []);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            gap="20px"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Segment 2 Information
              <Tooltip
                title={tooltipComponent(customerSegmentsTooltip.header1)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editSegment2(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editSegment2(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Segment Code
                <Tooltip
                  title={tooltipComponent(customerSegmentsTooltip.segment2Code)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="segmentCode"
                variant="outlined"
                placeholder="Segment Code"
                error={segmentCodeError}
                helperText={
                  segmentCodeError
                    ? validationMessages.customerSegment2
                    : customerSegmentsTooltip.segment2Code
                }
                value={segmentCode ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setSegmentCode(e.target.value.toUpperCase());
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Segment Name
                <Tooltip
                  title={tooltipComponent(customerSegmentsTooltip.segment2Name)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="segmentName"
                variant="outlined"
                placeholder="Segment Name"
                error={segmentNameError}
                helperText={
                  segmentNameError
                    ? validationMessages.customerDescription
                    : customerSegmentsTooltip.segment2Name
                }
                value={segmentName ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setSegmentName(e.target.value);
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Guest Type Code
                <Tooltip
                  title={tooltipComponent(
                    customerSegmentsTooltip.guestTypeCode2,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="guestTypeCode"
                disablePortal
                options={removeSampleTestArray(guestTypeList)}
                disabled={!guestTypeList}
                value={guestTypeCode ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => setGuestTypeCode(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Guest Type Code" />
                )}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Parent Segment 1 Code
                <Tooltip
                  title={tooltipComponent(
                    customerSegmentsTooltip.parentSegment1,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="parentSegment1Code"
                disablePortal
                options={removeSampleTestArray(segmentList)}
                disabled={!segmentList}
                value={parentSegment ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => setParentSegment(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Parent Segment 1 Code" />
                )}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip
                title={tooltipComponent(customerSegmentsTooltip.active1)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="routeLocalCalls"
                    checked={routeLocalCalls}
                    onChange={(e: any) => setRouteLocalCalls(e.target.checked)}
                  />
                }
                label="Route Local Calls ?"
              />
              <Tooltip
                title={tooltipComponent(
                  customerSegmentsTooltip.routeLocalCalls,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updateSegment2 : addCustomerSegmentInformation
              }
              id="addNewCustomerSegment2Information"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Segment 2
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">
              Customer Segment Information Available
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {segment2 ? (
                <DraggableItemList
                  itemData={segment2}
                  onOrderUpdate={(order) => setSegment2(order)}
                  onEdit={(info) => {
                    editSegment2(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteCustomerSegmentInformation(id)}
                  code={DNDFieldsEnum.SEGMENT2CODE}
                  name={DNDFieldsEnum.SEGMENT2NAME}
                />
              ) : (
                <Typography variant="body2">
                  No Customer Segment Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Segment2;
